import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { CommentsPermissions } from '../../comments/comments-permissions-context';
import { CommentsPermissionsContext } from '../../comments/comments-permissions-context';
import { useJobArchitectureOptions } from '../api/use-job-architecture-options';

export type JobArchitecturePermissions = {
    readonly canModify: boolean;
    readonly canRead: boolean;
};

const defaultPermissions: JobArchitecturePermissions = {
    canModify: false,
    canRead: false,
};

const JobArchitecturePermissionsContext = createContext<JobArchitecturePermissions>(defaultPermissions);

export const JobArchitecturePermissionsValueProvider: FunctionComponent<
    PropsWithChildren<{ readonly value: JobArchitecturePermissions }>
> = ({ children, value }) => {
    const commentsPermissions = useMemo<CommentsPermissions>(
        () => ({ canResolveThreads: value.canRead }),
        [value.canRead],
    );

    return (
        <JobArchitecturePermissionsContext.Provider value={value}>
            <CommentsPermissionsContext.Provider value={commentsPermissions}>
                {children}
            </CommentsPermissionsContext.Provider>
        </JobArchitecturePermissionsContext.Provider>
    );
};

export const JobArchitecturePermissionsContextProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const { data } = useJobArchitectureOptions();

    const permissions: JobArchitecturePermissions = !is.nullOrUndefined(data)
        ? {
              canModify: data.canCreate,
              canRead: data.canRead,
          }
        : defaultPermissions;

    return (
        <JobArchitecturePermissionsValueProvider value={permissions}>
            {children}
        </JobArchitecturePermissionsValueProvider>
    );
};

export const useJobArchitecturePermissions = (): JobArchitecturePermissions => {
    const context = useContext(JobArchitecturePermissionsContext);

    if (context === null) {
        throw new Error('useJobArchitecturePermissions must be used within JobArchitecturePermissionsContextProvider');
    }

    return context;
};
